import { IonInput } from '@ionic/react';
import classnames from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import PhoneNumberInput from '../../../../components-library/PhoneNumberInput';
import ChannelChooser from '../../../../components-library/auth/ChannelChooser';
import { LoginSchema, loginSchema } from './schema';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../../store/types';
import { OTP_DELIVERY_CHANNELS, REDUX_STORE_SLICES } from '../../../../services/exports/Constants';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '../../components/buttons/Button';
import { useTranslation } from 'react-i18next';
import { PropsWithChildren, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import useTheme from '../../../../hooks/ui/useTheme';
import useOtpChannels from '../../../../hooks/auth/useOtpChannels';

interface LoginFormProps extends PropsWithChildren {
  onSubmit: (data: LoginSchema & { delivery_channel: OTP_DELIVERY_CHANNELS }) => void;
  loading: boolean;
}

const LoginForm = ({ onSubmit, loading, children }: LoginFormProps) => {
  const companyId = useSelector((state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA].company?.id);
  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      phone_number: '',
      email: '',
      company_id: companyId,
    },
  });
  // From the provider, not a constant: an email-only partner offers no WhatsApp, and
  // pre-selecting it would make the first request a 422.
  const { channels, defaultChannel } = useOtpChannels();
  const [selectedChannel, setSelectedChannel] = useState<OTP_DELIVERY_CHANNELS>(defaultChannel);

  // `initialData` is persisted, so the first paint can run against a cached company that
  // predates `otp_delivery_channels`. Without this the stale selection survives the fresh
  // company landing, and the address field never appears for an email-only shop.
  useEffect(() => {
    if (!channels.includes(selectedChannel)) {
      setSelectedChannel(defaultChannel);
    }
  }, [channels, selectedChannel, defaultChannel]);
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Modals:Login:LoginForm' });
  const theme = useTheme();

  const isEmailChannel = selectedChannel === OTP_DELIVERY_CHANNELS.EMAIL;

  const _onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form.getValues().phone_number) {
      toast.error(t('feedback.phone_number_required'));
    }

    if (isEmailChannel && !form.getValues().email) {
      toast.error(t('feedback.email_required'));

      return;
    }

    // The address is only meaningful on the email channel; dropping it elsewhere keeps
    // the SMS and WhatsApp payloads byte for byte what they were.
    form.handleSubmit(({ email, ...data }) =>
      onSubmit({ ...data, delivery_channel: selectedChannel, ...(isEmailChannel && { email }) }),
    )(e);
  };

  return (
    <>
      <form className="space-y-4 text-center" onSubmit={_onSubmit}>
        <div>
          <Controller
            name="phone_number"
            control={form.control}
            render={({ field: { ref: _ref, ...rest }, fieldState }) => (
              <PhoneNumberInput
                inputContainerStyle="!px-4 !h-[46px]"
                placeholder={t('placeholders.mobile_number')}
                {...rest}
                validationErrors={fieldState?.error?.message ? [fieldState.error?.message] : []}
              />
            )}
          />
        </div>
        <ChannelChooser value={selectedChannel} onChange={setSelectedChannel} />
        {isEmailChannel && (
          <Controller
            name="email"
            control={form.control}
            render={({ field: { ref: _ref, ...field }, fieldState }) => (
              <div className="w-full text-left">
                <IonInput
                  type="email"
                  className={classnames('bordered !px-4', { 'border-danger border-xs': !!fieldState.error?.message })}
                  style={{ '--placeholder-color': theme.color.mobile.tertiary }}
                  placeholder={t('placeholders.email')}
                  data-test="otp-email"
                  {...field}
                  // Per keystroke, not on blur: _onSubmit reads getValues(), so with
                  // onIonChange a customer who typed and tapped Send without leaving the
                  // field got the "enter your email" toast on a filled field.
                  onIonInput={field.onChange}
                />
                <p className="text-danger text-xs">{fieldState.error?.message}</p>
              </div>
            )}
          />
        )}
        {children && children}
        <Button
          title={t('buttons.send_code')}
          type="submit"
          expand="block"
          loading={loading}
          className="[&::part(native)]:-ml-[2px]"
        />
      </form>
    </>
  );
};

export default LoginForm;
